<template>
    <div class="flex-taxonomy-data__section flex-taxonomy-data__section--multi flex-taxonomy-data__category">
        <div class="flex-taxonomy-data__section-column flex-form__field">
            <div class="flex-form__label">
                Category
            </div>
            <div v-show="!nameEditable">
                <span class="flex-taxonomy-data__category-name"> {{ category.name }}</span>
                <button class="flex-taxonomy-data__button--add-link" @click="editCategory()">
                    Edit
                </button>
            </div>
            <div v-show="nameEditable" class="flex-taxonomy-data__category-input">
                <vue-autosuggest
                    v-model="categoryKeywords"
                    class="flex-taxonomy-data__autosuggest"
                    :suggestions="filteredCategoryOptions"
                    :input-props="{ class: 'flex-taxonomy-data__input' }"
                    :get-suggestion-value="getSuggestionValue"
                    @input="onCategoryInput"
                    @selected="onCategorySelect"
                >
                    <template slot-scope="{suggestion}">
                        <span class="flex-taxonomy-data__autosuggest-item">{{ suggestion.item.name }}</span>
                    </template>
                </vue-autosuggest>
                <button v-show="category.name !== '' && filteredCategoryOptions[0] && filteredCategoryOptions[0].data && filteredCategoryOptions[0].data.length === 0" class="flex-taxonomy-data__save" @click="saveCategory()">
                    CREATE
                </button>
            </div>
        </div>
        <div v-show="!nameEditable" class="flex-taxonomy-data__section flex-taxonomy-data__section-column flex-taxonomy-data__section--multi flex-form__field">
            <div class="flex-form__label">
                Tags
            </div>
            <div>
                <tags-editor
                    v-show="category.name !== ''"
                    v-model="category.tags"
                    :root="category.id"
                    :create-new-enabled="isCreateNewEnabled"
                    @delete="onTagRemove"
                />
            </div>
        </div>
        <div>
            <span class="flex-taxonomy-data__button">
                <button class="flex-taxonomy-data__button--add" @click="deleteCategory()">DELETE</button>
            </span>
        </div>
    </div>
</template>

<script>
import { VueAutosuggest } from 'vue-autosuggest';
import { mapMutations } from 'vuex';
import TagsEditor from '@/components/utilities/tags-editor';
import 'vue-simple-suggest/dist/styles.css';
import taxonomyCategories from '@/graphql/queries/taxonomy-categories.gql';
import * as service from '@/sitecore/sitecore-data-service';
import { SHOW_ALERT } from '@/store/types/mutation-types';

export default {
    name: 'taxonomy-category',

    components: {
        VueAutosuggest,
        TagsEditor,
    },

    apollo: {
        categorySearchResults: {
            query: taxonomyCategories,
            variables() {
                return {
                    templateId: this.templateId,
                };
            },
            update(data) {
                return data.search && data.search.results ? data.search.results.items : [];
            },
            fetchPolicy: 'no-cache',
        },
    },

    props: {
        category: {
            type: Object,
            default: () => ({ name: '', tags: [], id: '' }),
        },
    },

    data() {
        return {
            autoCompleteStyle: {
                inputWrapper: '',
                defaultInput: 'flex-form__input',
                suggestions: 'flex-taxonomy-data__autocomplete-results',
                suggestItem: 'flex-taxonomy-data__autocomplete-result',
            },
            tagKeywords: '',
            categoryKeywords: '',
            nameEditable: true,
            taxonomyRootId: 'c2cff99cb5e9461996b564ea359f0dcb',
            templateId: 'c388f2814d88415b81c2822c06890164',
        };
    },

    computed: {
        filteredCategoryOptions() {
            return [
                {
                    data: this.categorySearchResults ? this.categorySearchResults.filter((item) => {
                        if (!this.categoryKeywords) return true;
                        return item.name.toLowerCase().includes(this.categoryKeywords);
                    }) : {},
                },
            ];
        },

        isCreateNewEnabled() {
            return this.category && this.category.path ? this.category.path.indexOf('/sitecore/content/global settings/taxonomy') > -1 : false;
        },

    },

    created() {
        this.nameEditable = this.category.name === '';
    },

    methods: {
        ...mapMutations({
            showAlert: SHOW_ALERT,
        }),

        getSuggestionValue(val) {
            return val.item.name;
        },

        onCategoryInput(text) {
            this.category.name = text;
            this.category.id = '';
            this.category.path = '';
        },

        onCategorySelect(tag) {
            if (tag) {
                this.category.name = tag.item.name;
                this.category.id = tag.item.id;
                this.category.path = tag.item.path;
                this.categoryKeywords = tag.item.name;
            }
            this.$emit('input', this.category);
            this.nameEditable = false;
        },

        onTagRemove(tagIndex) {
            this.category.tags.splice(tagIndex, 1);
        },

        async saveCategory() {
            if (this.category) {
                if (this.category.name && this.category.id === '') {
                    const response = await service.createTaxonomyTag(this.category.name, this.taxonomyRootId, this.templateId);

                    if (response.status === 'error') {
                        this.showAlert({ description: `Error updating item: ${ response.error }` });
                    }

                    this.category.id = response.data.id;
                    this.categorySearchResults = '';
                    this.category.tags = [];

                    if (this.$apollo.queries.categorySearchResults) {
                        await this.$apollo.queries.categorySearchResults.refetch();
                    }
                }

                if (this.category.name !== '') {
                    this.nameEditable = false;
                }
            }
        },

        editCategory() {
            this.nameEditable = true;
            this.category.tags = [];
        },

        deleteCategory() {
            this.$emit('deleted', this.category);
        },
    },
};
</script>

<style lang="scss">
@import '@/styles/_includes.scss';

.flex-taxonomy-data {
    margin: 25px 35px 75px;

    &__form {
        max-width: 100%;
        margin-bottom: 40px;
    }

    &__buttons {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;

        display: flex;
        padding: 0;
        margin: 0;
        z-index: 10;
    }

    &__category-name {
        font-size: large;
        margin-right: 10px;
    }

    &__category {
        border: 1px solid var(--form-inputBorderColor);
        padding: 10px;
        margin-bottom: 10px;
    }

    &__button {
        flex-grow: 1;
        z-index: 2; // issue with z-index for toggles going over button
        min-height: 50px;

        &--continue {
            @extend %button;
        }

        &--cancel {
            @extend %button-alt;
        }

        &--add {
            @extend %button;
            background-color: var(--form-buttonBackgroundColor);
            max-width: fit-content;
        }
    }

    &__category-input {
        display: flex;
        flex-wrap: wrap;
        width: 100%;
    }

    &__autosuggest {
        position: relative;

        .autosuggest__results-container {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            z-index: 1000;
        }

        .autosuggest__results {
            max-height: 14em;
            overflow: auto;
            background-color: var(--form-inputBackgroundColor);
            border: 1px solid var(--form-inputBorderColor);
        }
    }

    &__autosuggest-item {
        display: block;
        font-size: 0.875rem;
        padding: 0.375em 0.5em;
        color: var(--form-inputColor);
        text-transform: initial;

        &:hover {
            cursor: pointer;
            color: var(--form-inputHoverColor);
            background-color: var(--form-inputHoverBackgroundColor);
        }
    }

    &__tags {
        display: flex;
        flex-wrap: wrap;
    }

    &__tag {
        margin-top: 1em;
        margin-right: 1em;
    }

    &__tag-button {
        display: flex;
        align-items: center;
        font-size: 0.75rem;
        padding: 0.375em 0.75rem;
        font-weight: 600;
        color: var(--form-buttonColor);
        background-color: var(--form-buttonBackgroundColor);
        border-radius: 10px;

        &:hover {
            color: var(--form-buttonHoverColor);
            background-color: var(--form-buttonHoverBackgroundColor);
        }
    }

    &__tag-icon {
        font-size: 1.125em;
        margin-left: 0.75em;
    }

    &__tagless {
        font-size: 0.75rem;
        margin-top: 0.5em;
        font-style: italic;
        color: var(--form-controlColor);
    }

    &__tag-error {
        font-size: 0.75rem;
        margin-top: 0.5em;
        font-style: italic;
        color: var(--form-deleteLinkColor);
    }

    &__tags-input {
        display: flex;
        flex-wrap: wrap;
    }

    &__save {
        display: flex;
        align-items: center;
        font-size: 0.75rem;
        padding: 0.375em 0.75rem;
        font-weight: 600;
        color: var(--form-buttonColor);
        background-color: var(--form-buttonBackgroundColor);
    }

    &__input {
        color: var(--form-inputColor);
        background-color: var(--form-inputBackgroundColor);
        border: 1px solid var(--form-inputBorderColor);
        font-size: 0.875rem;
        width: 100%;
        max-width: 28em;
        padding: 0.5rem 0.75rem;
    }

        &__section {
        padding-bottom: 1.5rem;

        &--multi {
            display: flex;
        }
    }

    &__section-column {
        width: 100%;

        &:first-child { padding-right: 1em; }
        &:last-child { padding-left: 1em; }
    }

}
</style>
